import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AddAltDontuse } from '@clds/icon';
import Popover from '@clds/popover';
import { ColorPicker } from './ColorPicker';
import { usePrevious } from './hooks/usePrevious';
import { colorPickerTheme } from './theme';

const AddIcon = styled(AddAltDontuse).attrs({ tabIndex: '0' })`
  cursor: pointer;
  ${colorPickerTheme.item.sizeVariant.sm};
  display: inline-block;
  vertical-align: top;
`;

/**
 * `ColorPicker`, a `CustomPicker` build upon `react-colors` controlled HOC
 * wrapped with `Popover`, allow the user to open/close and choose a color
 * @param color: string
 * @param onChange: (i: string) => void
 * @param children?: ReactNode | Array<ReactNode>
 * @param comp: ReactNode
 * @param onChangeComplete?: (color: ColorResult) => void
 * @param dataTestSpecifier?: string
 * @param variant: 'default' | 'simpleHex'
 * @returns {JSX.Element}
 * @constructor
 */
export const ColorPickerPopover = ({
  color,
  onChange,
  children,
  onChangeComplete,
  triggeredElement,
  'data-test-specifier': dataTestSpecifier,
  variant,
  ...rest
}) => {
  const [visible, setVisible] = useState(false);
  const prevVisibleState = usePrevious(visible);

  const handleVisibleChange = useCallback(() => {
    setVisible((visible) => !visible);
  }, []);

  useEffect(() => {
    if (prevVisibleState && !visible) {
      onChangeComplete?.(color);
    }
  }, [color, onChangeComplete, prevVisibleState, visible]);

  const colorPicker = <ColorPicker color={color} onChange={onChange} data-test-specifier={dataTestSpecifier} variant={variant} />;

  return (
    <Popover
      overlay={<div data-test="color-picker-popover-overlay">{children ? children({ visible, setVisible, colorPicker }) : colorPicker}</div>}
      paperProps={{ isPadded: false }}
      visible={visible}
      onVisibleChange={handleVisibleChange}
      placement="bottom"
      trigger="click"
      {...rest}
      data-test-specifier={dataTestSpecifier}
    >
      <span data-test="color-picker-popover-trigger" data-test-specifier={dataTestSpecifier}>
        {triggeredElement || <AddIcon />}
      </span>
    </Popover>
  );
};
